import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import {
  employerPostingPreferencesForOrgApi,
  emptyEmployerPostingPreferences,
  parseEmployerPostingPreferences,
  type EmployerPostingPreferences,
} from '@/lib/employer-posting-preferences'

const ONBOARDING_PREFERENCES_PATH = `${API_ORIGIN}/api/profiles/onboarding/preferences`

function orgPostingPreferencesPath(slug: string): string {
  return `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/posting-preferences/`
}

export async function fetchManagedOrganisationSlugs(): Promise<string[]> {
  const response = await authorizedFetch(`${API_ORIGIN}/api/organisations/managed`)
  if (!response.ok) {
    throw new Error('Failed to fetch organisations')
  }
  const orgs = (await response.json()) as Array<{ slug?: string }>
  return orgs.map((org) => org.slug?.trim() || '').filter(Boolean)
}

export async function resolveEmployerPostingPreferencesOrgSlug(
  preferredSlug?: string | null,
): Promise<string | null> {
  const trimmed = preferredSlug?.trim()
  if (trimmed) return trimmed

  try {
    const slugs = await fetchManagedOrganisationSlugs()
    return slugs[0] ?? null
  } catch {
    return null
  }
}

export async function loadEmployerPostingPreferences(opts: {
  orgSlug?: string | null
  defaultCountryCode?: string
}): Promise<{ preferences: EmployerPostingPreferences; orgSlug: string | null }> {
  const defaultCountry = opts.defaultCountryCode ?? ''
  const orgSlug = await resolveEmployerPostingPreferencesOrgSlug(opts.orgSlug)

  if (orgSlug) {
    const response = await authorizedFetch(orgPostingPreferencesPath(orgSlug))
    if (response.ok) {
      const data = await response.json()
      return {
        preferences: parseEmployerPostingPreferences(data.preferences, defaultCountry),
        orgSlug,
      }
    }
  }

  const sessionResponse = await authorizedFetch(ONBOARDING_PREFERENCES_PATH)
  if (sessionResponse.ok) {
    const data = await sessionResponse.json()
    return {
      preferences: parseEmployerPostingPreferences(data.preferences, defaultCountry),
      orgSlug: null,
    }
  }

  return {
    preferences: emptyEmployerPostingPreferences(defaultCountry),
    orgSlug: null,
  }
}

export async function saveEmployerPostingPreferences(opts: {
  preferences: EmployerPostingPreferences
  orgSlug?: string | null
}): Promise<{ orgSlug: string | null }> {
  const orgSlug = await resolveEmployerPostingPreferencesOrgSlug(opts.orgSlug)

  if (orgSlug) {
    const response = await authorizedFetch(orgPostingPreferencesPath(orgSlug), {
      method: 'PUT',
      body: JSON.stringify({ preferences: employerPostingPreferencesForOrgApi(opts.preferences) }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        (typeof errorData.error === 'string' && errorData.error) || 'Failed to save preferences',
      )
    }
    return { orgSlug }
  }

  const response = await authorizedFetch(ONBOARDING_PREFERENCES_PATH, {
    method: 'POST',
    body: JSON.stringify({ preferences: employerPostingPreferencesForOrgApi(opts.preferences) }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      (typeof errorData.error === 'string' && errorData.error) || 'Failed to save preferences',
    )
  }
  return { orgSlug: null }
}

export async function loadOrganisationPostingPreferences(
  slug: string,
  defaultCountryCode = '',
): Promise<EmployerPostingPreferences> {
  const response = await authorizedFetch(orgPostingPreferencesPath(slug))
  if (!response.ok) {
    throw new Error('Failed to load posting preferences')
  }
  const data = await response.json()
  return parseEmployerPostingPreferences(data.preferences, defaultCountryCode)
}

export async function saveOrganisationPostingPreferences(
  slug: string,
  preferences: EmployerPostingPreferences,
): Promise<EmployerPostingPreferences> {
  const response = await authorizedFetch(orgPostingPreferencesPath(slug), {
    method: 'PUT',
    body: JSON.stringify({ preferences: employerPostingPreferencesForOrgApi(preferences) }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      (typeof errorData.error === 'string' && errorData.error) || 'Failed to save preferences',
    )
  }
  const data = await response.json()
  return parseEmployerPostingPreferences(data.preferences)
}
